import { zodResolver } from '@hookform/resolvers/zod';
import { ArrowLeft, Loader2, Save } from 'lucide-react';
import { useEffect } from 'react';
import { useForm, type Control, type FieldPath } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { FlagDropdown } from '@/components/ui/flag-dropdown';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { Switch } from '@/components/ui/switch';
import { useAuthStore } from '@/auth/store';
import {
  useCreateVessel,
  useUpdateVessel,
  useVessel,
} from '@/features/vessels/queries';
import {
  vesselFormSchema,
  type VesselFormValues,
} from '@/features/vessels/schema';
import {
  CLASS_SOCIETIES,
  SENSITIVITY_LEVELS,
  VESSEL_CATEGORIES,
  type Vessel,
} from '@/features/vessels/types';
import { ApiError } from '@/lib/api';

/**
 * Helper that consolidates label + Input + error message for a single
 * scalar text field. Avoids ~20 repetitions of FormField/FormItem
 * boilerplate inside the page render.
 */
function TextField({
  control,
  name,
  label,
  hint,
  type = 'text',
  placeholder,
}: {
  control: Control<VesselFormValues>;
  name: FieldPath<VesselFormValues>;
  label: string;
  hint?: string;
  type?: 'text' | 'number';
  placeholder?: string;
}) {
  return (
    <FormField
      control={control}
      name={name}
      render={({ field }) => (
        <FormItem>
          <FormLabel>{label}</FormLabel>
          <FormControl>
            <Input
              type={type}
              placeholder={placeholder}
              {...field}
              value={
                field.value === null || field.value === undefined
                  ? ''
                  : String(field.value)
              }
            />
          </FormControl>
          {hint && <p className="text-xs text-muted-foreground">{hint}</p>}
          <FormMessage />
        </FormItem>
      )}
    />
  );
}

/** Native <select> styled to match the Input primitive. */
function SelectField({
  control,
  name,
  label,
  options,
  placeholderOption,
}: {
  control: Control<VesselFormValues>;
  name: FieldPath<VesselFormValues>;
  label: string;
  options: { value: string; label: string }[];
  placeholderOption?: string;
}) {
  return (
    <FormField
      control={control}
      name={name}
      render={({ field }) => (
        <FormItem>
          <FormLabel>{label}</FormLabel>
          <FormControl>
            <select
              className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
              {...field}
              value={
                field.value === null || field.value === undefined
                  ? ''
                  : String(field.value)
              }
            >
              {placeholderOption && <option value="">{placeholderOption}</option>}
              {options.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </FormControl>
          <FormMessage />
        </FormItem>
      )}
    />
  );
}

/** Build form defaults from a vessel (edit) or empty (create). */
function vesselToFormValues(
  v: Vessel | undefined,
  defaultCompanyId: string,
): VesselFormValues {
  return {
    name: v?.name ?? '',
    companyId: v?.companyId ?? defaultCompanyId,
    imo: v?.imo ?? null,
    mmsi: v?.mmsi ?? null,
    callSign: v?.callSign ?? null,
    flag: v?.flag ?? null,
    category: v?.category ?? 'civilian',
    sensitivityLevel: v?.sensitivityLevel ?? 'unclassified',
    nsnOtan: v?.nsnOtan ?? null,
    displayLabel: v?.displayLabel ?? null,
    lengthM: v?.lengthM ? Number(v.lengthM) : null,
    beamM: v?.beamM ? Number(v.beamM) : null,
    grossTonnage: v?.grossTonnage ?? null,
    yearBuilt: v?.yearBuilt ?? null,
    shipyard: v?.shipyard ?? null,
    classSociety: v?.classSociety ?? null,
    classNumber: v?.classNumber ?? null,
    classSurveyed: v?.classSurveyed ?? false,
    homePort: v?.homePort ?? null,
    isActive: v?.isActive ?? true,
  };
}

export function VesselFormPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const isEdit = Boolean(id);
  const user = useAuthStore((s) => s.user);
  const defaultCompanyId = user?.companyId ?? '';

  const { data: existing, isLoading: isLoadingExisting } = useVessel(id);
  const createMut = useCreateVessel();
  const updateMut = useUpdateVessel(id ?? '');
  const mutating = createMut.isPending || updateMut.isPending;
  const mutationError = createMut.error ?? updateMut.error;

  const form = useForm<VesselFormValues>({
    resolver: zodResolver(vesselFormSchema),
    defaultValues: vesselToFormValues(undefined, defaultCompanyId),
  });

  // Re-hydrate form when the vessel loads in edit mode.
  useEffect(() => {
    if (isEdit && existing) {
      form.reset(vesselToFormValues(existing, defaultCompanyId));
    }
  }, [isEdit, existing, defaultCompanyId, form]);

  const errorMessage = (() => {
    if (!mutationError) return null;
    if (mutationError instanceof ApiError) {
      return t(mutationError.key, {
        defaultValue: t('vessel.form.saveError', {
          defaultValue: 'Save failed',
        }),
        ...(mutationError.params ?? {}),
      });
    }
    return t('vessel.form.saveError', { defaultValue: 'Save failed' });
  })();

  const onSubmit = form.handleSubmit((values) => {
    if (isEdit && id) {
      updateMut.mutate(values, {
        onSuccess: (updated) => navigate(`/vessels/${updated.id}`),
      });
    } else {
      createMut.mutate(values, {
        onSuccess: (created) => navigate(`/vessels/${created.id}`),
      });
    }
  });

  const watchCategory = form.watch('category');


  if (isEdit && isLoadingExisting) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-9 w-64" />
        <Skeleton className="h-64 w-full" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  const cancelTo = isEdit && id ? `/vessels/${id}` : '/vessels';

  return (
    <div className="space-y-6">
      <header className="flex items-center gap-2">
        <Button asChild variant="ghost" size="icon" aria-label={t('common.back')}>
          <Link to={cancelTo}>
            <ArrowLeft aria-hidden />
          </Link>
        </Button>
        <h1 className="text-2xl font-semibold">
          {t(isEdit ? 'vessel.form.editTitle' : 'vessel.form.createTitle')}
        </h1>
      </header>

      {errorMessage && (
        <Alert variant="destructive">
          <AlertTitle>{t('vessel.form.saveError', { defaultValue: 'Save failed' })}</AlertTitle>
          <AlertDescription>{errorMessage}</AlertDescription>
        </Alert>
      )}

      <Form {...form}>
        <form onSubmit={onSubmit} className="space-y-4" noValidate>
          {/* Identity */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base">
                {t('vessel.detail.sectionIdentity')}
              </CardTitle>
            </CardHeader>
            <CardContent className="grid gap-4 sm:grid-cols-2">
              <TextField
                control={form.control}
                name="name"
                label={t('vessel.fields.name')}
              />
              <TextField
                control={form.control}
                name="imo"
                label={t('vessel.fields.imo')}
                hint={t('vessel.fields.imoHint')}
              />
              <TextField
                control={form.control}
                name="mmsi"
                label={t('vessel.fields.mmsi')}
              />
              <TextField
                control={form.control}
                name="callSign"
                label={t('vessel.form.callSign')}
              />
              <FormField
                control={form.control}
                name="flag"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('vessel.form.flag')}</FormLabel>
                    <FormControl>
                      <FlagDropdown
                        value={field.value ?? null}
                        onChange={(code) => field.onChange(code)}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </CardContent>
          </Card>


          {/* Category & sensitivity */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base">
                {t('vessel.detail.sectionCategory')}
              </CardTitle>
            </CardHeader>
            <CardContent className="grid gap-4 sm:grid-cols-2">
              <SelectField
                control={form.control}
                name="category"
                label={t('vessel.fields.category')}
                options={VESSEL_CATEGORIES.map((v) => ({
                  value: v,
                  label: t(`vessel.category.${v}`),
                }))}
              />
              <SelectField
                control={form.control}
                name="sensitivityLevel"
                label={t('vessel.fields.sensitivityLevel')}
                options={SENSITIVITY_LEVELS.map((v) => ({
                  value: v,
                  label: t(`vessel.sensitivity.${v}`),
                }))}
              />
              {watchCategory === 'armed' && (
                <>
                  <TextField
                    control={form.control}
                    name="nsnOtan"
                    label={t('vessel.fields.nsnOtan')}
                  />
                  <TextField
                    control={form.control}
                    name="displayLabel"
                    label={t('vessel.fields.displayLabel')}
                  />
                </>
              )}
            </CardContent>
          </Card>


          {/* Physical */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base">
                {t('vessel.detail.sectionPhysical')}
              </CardTitle>
            </CardHeader>
            <CardContent className="grid gap-4 sm:grid-cols-2">
              <TextField
                control={form.control}
                name="lengthM"
                label={t('vessel.fields.lengthM')}
                type="number"
              />
              <TextField
                control={form.control}
                name="beamM"
                label={t('vessel.fields.beamM')}
                type="number"
              />
              <TextField
                control={form.control}
                name="grossTonnage"
                label={t('vessel.fields.grossTonnage')}
                type="number"
              />
              <TextField
                control={form.control}
                name="yearBuilt"
                label={t('vessel.fields.yearBuilt')}
                type="number"
              />
              <TextField
                control={form.control}
                name="shipyard"
                label={t('vessel.fields.shipyard')}
              />
            </CardContent>
          </Card>

          {/* Classification */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base">
                {t('vessel.detail.sectionClassification')}
              </CardTitle>
            </CardHeader>
            <CardContent className="grid gap-4 sm:grid-cols-2">
              <SelectField
                control={form.control}
                name="classSociety"
                label={t('vessel.fields.classSociety')}
                placeholderOption={t('vessel.form.none')}
                options={CLASS_SOCIETIES.map((s) => ({ value: s, label: s }))}
              />
              <TextField
                control={form.control}
                name="classNumber"
                label={t('vessel.fields.classNumber')}
              />
              <FormField
                control={form.control}
                name="classSurveyed"
                render={({ field }) => (
                  <FormItem className="flex flex-row items-center justify-between gap-3 space-y-0 rounded-md border p-3 sm:col-span-2">
                    <div className="space-y-0.5">
                      <FormLabel className="!mt-0">
                        {t('vessel.form.classSurveyed')}
                      </FormLabel>
                      <p className="text-xs text-muted-foreground">
                        {t('vessel.form.classSurveyedHelp')}
                      </p>
                    </div>
                    <FormControl>
                      <Switch
                        checked={Boolean(field.value)}
                        onCheckedChange={(c) => field.onChange(c)}
                      />
                    </FormControl>
                  </FormItem>
                )}
              />
            </CardContent>
          </Card>


          {/* Operational */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base">
                {t('vessel.detail.sectionOperational')}
              </CardTitle>
            </CardHeader>
            <CardContent className="grid gap-4 sm:grid-cols-2">
              <TextField
                control={form.control}
                name="homePort"
                label={t('vessel.fields.homePort')}
              />
              <FormField
                control={form.control}
                name="isActive"
                render={({ field }) => (
                  <FormItem className="flex flex-row items-center gap-3 space-y-0 rounded-md border p-3">
                    <FormControl>
                      <input
                        type="checkbox"
                        className="h-4 w-4 cursor-pointer accent-primary"
                        checked={Boolean(field.value)}
                        onChange={(e) => field.onChange(e.target.checked)}
                      />
                    </FormControl>
                    <FormLabel className="!mt-0 cursor-pointer">
                      {t('vessel.fields.isActive')}
                    </FormLabel>
                  </FormItem>
                )}
              />
            </CardContent>
          </Card>

          {/* Footer actions */}
          <div className="flex justify-end gap-2 pt-2">
            <Button asChild variant="ghost" disabled={mutating}>
              <Link to={cancelTo}>{t('common.cancel')}</Link>
            </Button>
            <Button type="submit" disabled={mutating}>
              {mutating ? (
                <Loader2 className="animate-spin" aria-hidden />
              ) : (
                <Save aria-hidden />
              )}
              {t(isEdit ? 'vessel.form.submitUpdate' : 'vessel.form.submitCreate')}
            </Button>
          </div>
        </form>
      </Form>
    </div>
  );
}
